import { ASSET_TYPE, PRESENTATION_ACTIONS } from '../../constants/Constants';
import { browser } from '../../helpers/BrowserHelper';
import { IframeHelper } from '../../helpers/IframeHelper';
import { interpolateUrlVariables } from '../../helpers/UrlHelper';
import { ScormMock } from '../../mocks/scorm.mock';
import { ViewerObservableDimensionsIframeDimensionsChangedEvent } from '../../types/ViewerEvents.type';
export const OBSERVABLE_DIMENSIONS_MIN_IFRAME_HEIGHT = 300;
export class ObservableDimensionsIframePresentationRenderer {
    constructor(assetViewerView, presentation) {
        this.assetViewerView = assetViewerView;
        this.presentation = presentation;
        this.container = null;
        this.width = 400;
        this.height = OBSERVABLE_DIMENSIONS_MIN_IFRAME_HEIGHT;
        this.scrollWidth = 0;
        this.scrollHeight = 0;
        // mark as active, since Iframes can have state, they can't be destroyed
        this.isActive = true;
        this.isLoading = false;
        this.resizeCheckInterval = null;
        /**
         * Element.closest() polyfill
         * https://developer.mozilla.org/en-US/docs/Web/API/Element/closest#Polyfill
         */
        if (!Element.prototype.closest) {
            if (!Element.prototype.matches) {
                Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
            }
            Element.prototype.closest = function (s) {
                const el = this;
                let ancestor = this;
                if (!document.documentElement.contains(el))
                    return null;
                do {
                    if (ancestor.matches(s))
                        return ancestor;
                    ancestor = ancestor.parentElement;
                } while (ancestor !== null);
                return null;
            };
        }
    }
    dispatchDimensionsChangedEvent(scrollWidth, scrollHeight) {
        this.assetViewerView.eventBus.dispatch(new ViewerObservableDimensionsIframeDimensionsChangedEvent({ scrollHeight, scrollWidth }));
    }
    get url() {
        const pageIndex = (this.presentation.details && this.presentation.details.firstPageIndex) || 0;
        return interpolateUrlVariables(this.presentation.urls[pageIndex], this.config);
    }
    get dimensions() {
        return { width: this.width, height: this.height };
    }
    get hasMetaData() {
        return !!this.dimensions;
    }
    get config() {
        return this.assetViewerView.config;
    }
    load() { }
    render(container) {
        this.container = container;
        return Promise.resolve().then(() => {
            this.renderIframe(container);
        });
    }
    renderIframe(container) {
        this.isLoading = true;
        const wrapper = document.createElement('div');
        const config = this.assetViewerView.config;
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        wrapper.style.overflow = 'hidden';
        wrapper.style.webkitOverflowScrolling = 'touch';
        wrapper.style.background = 'black';
        // iframe scroll hack for mobile safari
        if (browser.ios || (browser.safari && 'ontouchstart' in window)) {
            wrapper.style.overflow = 'auto';
        }
        wrapper.classList.add('iframe-wrapper');
        const iframe = (this.iframe = document.createElement('iframe'));
        iframe.classList.add('av-webapp-iframe');
        wrapper.appendChild(iframe);
        iframe.scrolling = 'yes';
        iframe.allowFullscreen = true;
        iframe.setAttribute('allow', config.IFRAME_ALLOW);
        container.appendChild(wrapper);
        if (this.assetViewerView.assetViewerItem.type === ASSET_TYPE.SCORM && this.config.SCORM_API_AVAILABLE === false) {
            this.scormMock = new ScormMock();
            this.scormMock.ensureAPI();
        }
        const onLoad = () => {
            // START needed for iFrame rendering of google docs inside chromium browsers (https://jira.showpad.io/browse/SP-27346)
            if (container) {
                const futureSlide = container.closest('.av-slide');
                if (futureSlide) {
                    if (futureSlide.style.width === '0px')
                        futureSlide.style.removeProperty('width');
                    if (futureSlide.style.height === '0px')
                        futureSlide.style.removeProperty('height');
                    if (futureSlide.style.visibility === 'hidden')
                        futureSlide.style.removeProperty('visibility');
                }
            }
            // END needed for iFrame rendering of google docs inside chromium browsers (https://jira.showpad.io/browse/SP-27346)
            this.isLoading = false;
            this.width = 400;
            this.height = OBSERVABLE_DIMENSIONS_MIN_IFRAME_HEIGHT;
            iframe.onerror = null;
            let iframeContentDocument = null;
            // avoid 'Access Denied' error in IE11
            try {
                iframeContentDocument = this.iframe.contentDocument;
            }
            catch (e) { }
            // Only do the following on same-origin iframes
            if (iframeContentDocument) {
                if (this.resizeCheckInterval) {
                    clearInterval(this.resizeCheckInterval);
                }
                this.resizeCheckInterval = setInterval(() => {
                    const iframeBody = iframe.contentDocument.body;
                    const scrollHeight = iframeBody.scrollHeight;
                    const scrollWidth = iframeBody.scrollWidth;
                    if (this.scrollHeight !== scrollHeight && scrollHeight > OBSERVABLE_DIMENSIONS_MIN_IFRAME_HEIGHT) {
                        this.scrollWidth = scrollWidth;
                        this.scrollHeight = scrollHeight;
                        this.width = scrollWidth;
                        this.height = scrollHeight;
                        this.dispatchDimensionsChangedEvent(scrollWidth, scrollHeight);
                    }
                }, 1000);
                IframeHelper.injectShowpadScripts(this.iframe, {
                    iframeUrl: this.url,
                    trackHtmlPageLocation: config.TRACK_HTML_PAGE_LOCATION_ENABLED && this.assetViewerView.assetViewerItem.type === ASSET_TYPE.WEBAPP,
                    showpadJsPath: config.SHOWPAD_JS_PATH,
                    user: config.showpadUser
                });
                if (this.presentation.json) {
                    try {
                        const payload = JSON.parse(this.presentation.json);
                        this.iframe.contentWindow.postMessage({
                            type: this.presentation.type,
                            payload
                        }, '*');
                    }
                    catch (e) {
                        throw Error('Could not parse presentation JSON and post it to iframe');
                    }
                }
                else if (this.presentation.jsonUrl) {
                    try {
                        this.iframe.contentWindow.postMessage({
                            action: PRESENTATION_ACTIONS[this.presentation.type],
                            origin: 'asset-viewer',
                            payload: this.presentation.jsonUrl
                        }, '*');
                    }
                    catch (e) {
                        throw Error('Could not post the json url to iframe');
                    }
                }
            }
        };
        const onError = error => {
            this.isLoading = false;
            iframe.onload = null;
            iframe.onerror = null;
            this.iframe = null;
        };
        iframe.onload = onLoad.bind(this);
        iframe.onerror = onError.bind(this);
        this.iframe.src = this.url;
    }
    destroy() {
        this.iframe = null;
        if (this.resizeCheckInterval) {
            clearInterval(this.resizeCheckInterval);
            this.resizeCheckInterval = null;
        }
        if (this.container) {
            this.container.innerHTML = '';
            this.container = null;
        }
    }
    destroyFinal() {
        this.destroy();
    }
    resize() {
        // reset height, so actual height calculation can be picked up by this.resizeCheckInterval
        this.scrollHeight = 0;
        this.height = OBSERVABLE_DIMENSIONS_MIN_IFRAME_HEIGHT;
    }
}
//# sourceMappingURL=ObservableDimensionsIframePresentationRenderer.js.map